"use client";

import React, { useEffect, useState } from "react";
import { contactAPI } from "../services/api";
import { formatPhonesMultiline } from "../utils/formatPhonesMultiline";
import { splitRepresentative } from "../utils/splitRepresentative";
import "./ContactPageStyle.css";
import { Smartphone, Mail, MapPin } from "lucide-react";
import { SeoHelmet } from "../components/SeoHelmet";
import LogoLoader from "../components/ui/LogoLoader";
import { DEFAULT_DESCRIPTION } from "../config/seo";
import InputMask from "react-input-mask";

const QR_ROSTOV = "https://max.ru/u/f9LHodD0cOJwdTWxPGV-7S_yLxZ5ey64qaW8vhZHZfxAEQINeUce8o6Q49w";
const QR_ULYANOVSK = "https://max.ru/u/f9LHodD0cOL4ZIczKvd8gcW3t1ztMUcj0zOLAtvjknr23IVEywYej40RqT8";
const QR_SIZE = 126;

interface ContactPageData {
  phone1?: string | null;
  email1?: string | null;
  address1?: string | null;
  city1?: string | null;
  phone2?: string | null;
  email2?: string | null;
  address2?: string | null;
  city2?: string | null;
}

const ContactPage: React.FC = () => {
  const [data, setData] = useState<ContactPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"success" | "error" | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const page = await contactAPI.getPage();
        setData(page);
      } catch (error) {
        console.error("Ошибка загрузки данных контактов:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" })); // сброс ошибки при вводе
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Валидация
    const newErrors = {
      name: !formData.name.trim() ? "Поле Имя обязательно" : "",
      email: !formData.email.trim() ? "Поле Email обязательно" : "",
      phone: !formData.phone.trim() ? "Поле Телефон обязательно" : "",
      message: !formData.message.trim() ? "Поле 'Что Вас интересует?' обязательно" : "",
    };

    setErrors(newErrors);

    // Если есть ошибки, не отправляем
    if (Object.values(newErrors).some(err => err !== "")) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await contactAPI.send(formData);
      setSubmitStatus("success");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error(error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <SeoHelmet title="Контакты" description={DEFAULT_DESCRIPTION} path="/contact" />
        <LogoLoader />
      </>
    );
  }
  if (!data) {
    return (
      <>
        <SeoHelmet title="Контакты" description={DEFAULT_DESCRIPTION} path="/contact" />
        <div className="empty-state">Данные контактов не найдены</div>
      </>
    );
  }

  const rostov = splitRepresentative(data.address1);

  return (
    <div className="contact-page">
      <SeoHelmet
        title="Контакты"
        description={`Свяжитесь с ТЕКС МОЛЛ: ${data.city1 ?? ""}, ${data.city2 ?? ""}. Телефоны, email и адреса.`}
        path="/contact"
      />
      <div className="contactsWrapper">
        <div className="contacts-align reveal-stagger">
        <div className="contacts-section reveal-child">
          <h4>{data.city1}</h4>
          <div className="contacts">
            <div className="footer-paragraph">
              <strong className="phoneIcon"><Smartphone /></strong>
              <p style={{ whiteSpace: "pre-line" }}>{formatPhonesMultiline(data.phone1)}</p>
            </div>
            <div className="footer-paragraph">
              <strong><Mail /></strong>
              <p className="contact-email">{data.email1}</p>
            </div>
            <div className="footer-paragraph">
              <strong><MapPin /></strong>
              <p style={{ whiteSpace: "pre-line" }}>{rostov.address}</p>
            </div>
          </div>
          <div className="city-qr-block">
            <a href={QR_ROSTOV} target="_blank" rel="noopener noreferrer" className="qr-item">
              <img src={`${process.env.PUBLIC_URL || ""}/images/qr-rostov.png`} alt="QR-код Ростов-на-Дону" width={QR_SIZE} height={QR_SIZE} />
            </a>
          </div>
        </div>

        <div className="contacts-section reveal-child">
          <h4>{data.city2}</h4>
          <div className="contacts">
            <div className="footer-paragraph">
              <strong className="phoneIcon"><Smartphone /></strong>
              <p style={{ whiteSpace: "pre-line" }}>{formatPhonesMultiline(data.phone2)}</p>
            </div>
            <div className="footer-paragraph">
              <strong><Mail /></strong>
              <p className="contact-email">{data.email2}</p>
            </div>
            <div className="footer-paragraph">
              <strong><MapPin /></strong>
              <p>{data.address2}</p>
            </div>
          </div>
          <div className="city-qr-block">
            <a href={QR_ULYANOVSK} target="_blank" rel="noopener noreferrer" className="qr-item">
              <img src={`${process.env.PUBLIC_URL || ""}/images/qr-ulyanovsk.png`} alt="QR-код Ульяновск" width={QR_SIZE} height={QR_SIZE} />
            </a>
          </div>
        </div>

        {rostov.representative && (
          <div className="contacts-section reveal-child">
            <h4>{rostov.representative.city}</h4>
            <div className="contacts">
              {rostov.representative.phone && (
                <div className="footer-paragraph">
                  <strong className="phoneIcon"><Smartphone /></strong>
                  <p>{rostov.representative.phone}</p>
                </div>
              )}
              {rostov.representative.email && (
                <div className="footer-paragraph">
                  <strong><Mail /></strong>
                  <p className="contact-email">{rostov.representative.email}</p>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>

      <div className="contactsFormWrapper reveal reveal-delay-2">
        <div className="contact-form-section">
          <form className="contact-form" onSubmit={handleSubmit}>
            <p>Оставьте сообщение, и мы свяжемся с вами в ближайшее время</p>
            <span>Обязательно заполните все поля формы, чтобы мы могли связаться с Вами.</span>

            <div className="form-group">
              <label htmlFor="name">Имя*</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Ваше имя"
                className={errors.name ? "input-error" : ""}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email*</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your@email.com"
                className={errors.email ? "input-error" : ""}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Телефон*</label>
              <InputMask
                mask="+7 (999) 999-99-99"
                value={formData.phone}
                onChange={handleInputChange}
              >
                {(inputProps: any) => (
                  <input
                    {...inputProps}
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="+7 (___) ___-__-__"
                    className={errors.phone ? "input-error" : ""}
                  />
                )}
              </InputMask>
            </div>

            <div className="form-group">
              <label htmlFor="message">Что Вас интересует?*</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={5}
                placeholder="Расскажите о ваших потребностях в тканях..."
                className={errors.message ? "input-error" : ""}
              />
            </div>

            {submitStatus === "success" && (
              <div className="form-message success">
                Сообщение отправлено!
              </div>
            )}
            {submitStatus === "error" && (
              <div className="form-message error">
                Ошибка отправки сообщения.
              </div>
            )}

            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Отправка..." : (
                <>
                  <span>Отправить</span>
                  <svg className="btn-luxury__arrow" viewBox="2.5 4.5 18 15" aria-hidden="true">
                    <path d="M4 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
